import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Chip, Tabs, Tab, Alert, Snackbar,
  Select, MenuItem, InputLabel, FormControl, Tooltip, List, ListItem,
  ListItemText, ListItemSecondaryAction, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Devices as DevicesIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Wifi as WifiIcon,
  HelpOutline as HelpIcon,
  FactCheck as FactCheckIcon,
  InfoOutlined,
} from '@mui/icons-material';
import {
  getWorkSites, createWorkSite, updateWorkSite, deleteWorkSite,
  getWorkSiteIpRules, addWorkSiteIpRule, deleteWorkSiteIpRule,
  getWorkSiteEmployees, assignWorkSiteEmployees, removeWorkSiteEmployee,
  getWorkSiteKioskDevices, registerKioskDevice, deactivateKioskDevice,
  getEmployees, setEmployeePin, clearEmployeePin,
  getCompanies, getPunchEvents,
} from '../api';
import { offlineTimeNotHonoured } from '../utils/punchDetails';
import { useConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../context/AuthContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// The device time a refused offline punch claimed: rejections that never got a
// valid instant carry it in the audit note, the rest use the stored punch time.
function claimedDeviceTime(ev) {
  const m = /rejected \w+; device time (\S+)/.exec(ev.notes || '');
  const raw = m ? m[1] : ev.punch_time;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
}

export default function WorkSiteManagement() {
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const { user } = useAuth();
  // Mirrors accessibleEmployeeIds() on the server: scoped callers only see
  // their direct reports (+ themselves) in a site's employee list.
  const employeeListScoped = !['program_admin', 'admin', 'payroll_admin'].includes(user?.role)
    && user?.permissions?.can_view_all_employees !== true;
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [punchEvents, setPunchEvents] = useState([]);
  const [punchEventsLoading, setPunchEventsLoading] = useState(false);
  const [punchFlaggedOnly, setPunchFlaggedOnly] = useState(false);

  // Edit form state
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip: '',
    latitude: '', longitude: '', geofence_radius_meters: 200,
    ip_enforcement_enabled: false, ip_enforcement_mode: 'hard',
    geo_enforcement_enabled: false, geo_enforcement_mode: 'hard',
    kiosk_enabled: false, kiosk_photo_required: false,
    default_company_id: '', manual_entry_mode: 'off',
  });
  const [companies, setCompanies] = useState([]);

  // Detail sub-data
  const [ipRules, setIpRules] = useState([]);
  const [siteEmployees, setSiteEmployees] = useState([]);
  const [kioskDevices, setKioskDevices] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [newIpCidr, setNewIpCidr] = useState('');
  const [newIpLabel, setNewIpLabel] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceUrl, setNewDeviceUrl] = useState('');
  const [newDeviceQr, setNewDeviceQr] = useState('');
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinEmployee, setPinEmployee] = useState(null);
  const [pinValue, setPinValue] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getWorkSites();
      setSites(res.data.filter(s => s.is_active));
    } catch (err) {
      showSnackbar('Failed to load work sites', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    getCompanies().then(res => setCompanies(res.data)).catch(() => {});
  }, [fetchSites]);

  // --- Site CRUD ---
  const handleOpenCreate = () => {
    setSelectedSite(null);
    setForm({
      name: '', address: '', city: '', state: '', zip: '',
      latitude: '', longitude: '', geofence_radius_meters: 200,
      ip_enforcement_enabled: false, ip_enforcement_mode: 'hard',
      geo_enforcement_enabled: false, geo_enforcement_mode: 'hard',
      kiosk_enabled: false, kiosk_photo_required: false,
      default_company_id: '', manual_entry_mode: 'off',
    });
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (site) => {
    setSelectedSite(site);
    setForm({
      name: site.name || '',
      address: site.address || '',
      city: site.city || '',
      state: site.state || '',
      zip: site.zip || '',
      latitude: site.latitude || '',
      longitude: site.longitude || '',
      geofence_radius_meters: site.geofence_radius_meters || 200,
      ip_enforcement_enabled: site.ip_enforcement_enabled || false,
      ip_enforcement_mode: site.ip_enforcement_mode || 'hard',
      geo_enforcement_enabled: site.geo_enforcement_enabled || false,
      geo_enforcement_mode: site.geo_enforcement_mode || 'hard',
      kiosk_enabled: site.kiosk_enabled || false,
      kiosk_photo_required: site.kiosk_photo_required || false,
      default_company_id: site.default_company_id || '',
      manual_entry_mode: site.manual_entry_mode || 'off',
    });
    setEditDialogOpen(true);
  };

  const handleSaveSite = async () => {
    try {
      const data = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        geofence_radius_meters: parseInt(form.geofence_radius_meters) || 200,
      };

      if (selectedSite) {
        await updateWorkSite(selectedSite.id, data);
        showSnackbar('Work site updated');
      } else {
        await createWorkSite(data);
        showSnackbar('Work site created');
      }
      setEditDialogOpen(false);
      fetchSites();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to save work site', 'error');
    }
  };

  const handleDeleteSite = async (site) => {
    const confirmed = await showConfirm({
      title: 'Deactivate Work Site',
      message: `Deactivate "${site.name}"? This will also deactivate all kiosk devices at this site.`,
      confirmText: 'Deactivate',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteWorkSite(site.id);
      showSnackbar('Work site deactivated');
      fetchSites();
    } catch (err) {
      showSnackbar('Failed to delete work site', 'error');
    }
  };

  // --- Detail Dialog ---
  const handleOpenDetail = async (site) => {
    setSelectedSite(site);
    setDetailTab(0);
    setDetailDialogOpen(true);
    setNewDeviceUrl('');
    setNewDeviceQr('');
    // Clear stale data from previous site
    setIpRules([]);
    setSiteEmployees([]);
    setKioskDevices([]);
    setPunchEvents([]);
    setPunchFlaggedOnly(false);

    // Load sub-data in parallel
    try {
      const [ipRes, empRes, devRes, allEmpRes] = await Promise.all([
        getWorkSiteIpRules(site.id),
        getWorkSiteEmployees(site.id),
        getWorkSiteKioskDevices(site.id),
        getEmployees(),
      ]);
      setIpRules(ipRes.data);
      setSiteEmployees(empRes.data);
      setKioskDevices(devRes.data);
      setAllEmployees(allEmpRes.data);
    } catch (err) {
      showSnackbar('Failed to load site details', 'error');
    }
  };

  // --- Punch Activity ---
  const fetchPunchEvents = useCallback(async (siteId, flaggedOnly) => {
    if (!siteId) return;
    setPunchEventsLoading(true);
    try {
      const res = await getPunchEvents({
        work_site_id: siteId,
        days: 14,
        ...(flaggedOnly ? { flagged: 'true' } : {}),
      });
      setPunchEvents(res.data);
    } catch (err) {
      showSnackbar('Failed to load punch activity', 'error');
    } finally {
      setPunchEventsLoading(false);
    }
  }, []);

  // Load punch activity lazily — only when its tab is opened.
  useEffect(() => {
    if (detailDialogOpen && detailTab === 3 && selectedSite) {
      fetchPunchEvents(selectedSite.id, punchFlaggedOnly);
    }
  }, [detailDialogOpen, detailTab, selectedSite, punchFlaggedOnly, fetchPunchEvents]);

  // --- IP Rules ---
  const handleAddIpRule = async () => {
    if (!newIpCidr.trim()) return;
    try {
      await addWorkSiteIpRule(selectedSite.id, { ip_cidr: newIpCidr.trim(), label: newIpLabel.trim() || null });
      const res = await getWorkSiteIpRules(selectedSite.id);
      setIpRules(res.data);
      setNewIpCidr('');
      setNewIpLabel('');
      showSnackbar('IP rule added');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Invalid IP/CIDR format', 'error');
    }
  };

  const handleDeleteIpRule = async (ruleId) => {
    try {
      await deleteWorkSiteIpRule(selectedSite.id, ruleId);
      setIpRules(prev => prev.filter(r => r.id !== ruleId));
      showSnackbar('IP rule removed');
    } catch (err) {
      showSnackbar('Failed to remove IP rule', 'error');
    }
  };

  // --- Employee Assignments ---
  const handleAssignEmployee = async (employeeId) => {
    try {
      await assignWorkSiteEmployees(selectedSite.id, [employeeId]);
      const res = await getWorkSiteEmployees(selectedSite.id);
      setSiteEmployees(res.data);
      showSnackbar('Employee assigned');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to assign employee', 'error');
    }
  };

  const handleUnassignEmployee = async (employeeId) => {
    try {
      await removeWorkSiteEmployee(selectedSite.id, employeeId);
      setSiteEmployees(prev => prev.filter(e => e.employee_id !== employeeId));
      showSnackbar('Employee unassigned');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to unassign employee', 'error');
    }
  };

  // --- Kiosk Devices ---
  const handleRegisterDevice = async () => {
    if (!newDeviceName.trim()) return;
    try {
      const res = await registerKioskDevice(selectedSite.id, { device_name: newDeviceName.trim() });
      setNewDeviceUrl(window.location.origin + res.data.kiosk_url);
      setNewDeviceQr(res.data.qr_code || '');
      setNewDeviceName('');
      const devRes = await getWorkSiteKioskDevices(selectedSite.id);
      setKioskDevices(devRes.data);
      showSnackbar('Device registered');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to register device', 'error');
    }
  };

  const handleDeactivateDevice = async (deviceId) => {
    const confirmed = await showConfirm({
      title: 'Deactivate Device',
      message: 'Deactivate this device?',
      confirmText: 'Deactivate',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deactivateKioskDevice(selectedSite.id, deviceId);
      const devRes = await getWorkSiteKioskDevices(selectedSite.id);
      setKioskDevices(devRes.data);
      showSnackbar('Device deactivated');
    } catch (err) {
      showSnackbar('Failed to deactivate device', 'error');
    }
  };

  const kioskUrlInputRef = useRef(null);

  const handleCopyUrl = () => {
    // Use the visible input element already in the DOM — this works on HTTP
    const input = kioskUrlInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
    input.setSelectionRange(0, 99999);
    document.execCommand('copy');
    showSnackbar('Copied to clipboard');
  };

  // --- PIN Management ---
  const handleOpenPinDialog = (emp) => {
    setPinEmployee(emp);
    setPinValue('');
    setPinDialogOpen(true);
  };

  const handleSetPin = async () => {
    if (!pinValue || !/^\d{4}$/.test(pinValue)) {
      showSnackbar('PIN must be exactly 4 digits', 'error');
      return;
    }
    try {
      await setEmployeePin(pinEmployee.employee_id, pinValue);
      // Refresh employee list
      const res = await getWorkSiteEmployees(selectedSite.id);
      setSiteEmployees(res.data);
      setPinDialogOpen(false);
      showSnackbar('PIN set');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to set PIN', 'error');
    }
  };

  const handleClearPin = async (employeeId) => {
    const confirmed = await showConfirm({
      title: 'Clear Kiosk PIN',
      message: 'Clear this employee\'s kiosk PIN?',
      confirmText: 'Clear PIN',
      confirmColor: 'warning',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await clearEmployeePin(employeeId);
      const res = await getWorkSiteEmployees(selectedSite.id);
      setSiteEmployees(res.data);
      showSnackbar('PIN cleared');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to clear PIN', 'error');
    }
  };

  const generateRandomPin = () => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setPinValue(pin);
  };

  // Unassigned employees for the dropdown
  const assignedIds = new Set(siteEmployees.map(e => e.employee_id));
  const unassignedEmployees = allEmployees.filter(e => !assignedIds.has(e.id));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">Work Sites</Typography>
          <Tooltip title="Manage physical locations where employees clock in/out. Configure IP restrictions, geofencing, and kiosk devices for each site.">
            <HelpIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Work Site
        </Button>
      </Box>

      {sites.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocationIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No work sites configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a work site to set up kiosk mode, IP restrictions, and geofencing
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Your First Work Site
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>IP Enforcement</TableCell>
                <TableCell>Geo Enforcement</TableCell>
                <TableCell>Kiosk</TableCell>
                <TableCell>Employees</TableCell>
                <TableCell>Devices</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.map(site => (
                <TableRow key={site.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpenDetail(site)}>
                  <TableCell><strong>{site.name}</strong></TableCell>
                  <TableCell>{[site.address, site.city, site.state].filter(Boolean).join(', ') || '—'}</TableCell>
                  <TableCell>
                    {site.ip_enforcement_enabled ? (
                      <Chip label={site.ip_enforcement_mode === 'hard' ? 'Hard Block' : 'Soft Flag'} size="small" color="primary" variant="outlined" />
                    ) : <Chip label="Off" size="small" />}
                  </TableCell>
                  <TableCell>
                    {site.geo_enforcement_enabled ? (
                      <Chip label={site.geo_enforcement_mode === 'hard' ? 'Hard Block' : 'Soft Flag'} size="small" color="secondary" variant="outlined" />
                    ) : <Chip label="Off" size="small" />}
                  </TableCell>
                  <TableCell>
                    {site.kiosk_enabled ? <Chip label="Enabled" size="small" color="success" /> : <Chip label="Off" size="small" />}
                  </TableCell>
                  <TableCell>{site.employee_count || 0}</TableCell>
                  <TableCell>{site.device_count || 0}</TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => handleOpenEdit(site)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteSite(site)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSite ? 'Edit Work Site' : 'New Work Site'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Site Name" fullWidth required sx={{ mt: 1, mb: 2 }}
            value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Address" fullWidth sx={{ mb: 2 }}
            value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} sx={{ flex: 2 }} />
            <TextField label="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} sx={{ flex: 1 }} />
            <TextField label="ZIP" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} sx={{ flex: 1 }} />
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Default Company</InputLabel>
            <Select
              value={form.default_company_id}
              label="Default Company"
              onChange={e => setForm(f => ({ ...f, default_company_id: e.target.value }))}
            >
              <MenuItem value="">None (use tenant default)</MenuItem>
              {companies.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Geofencing (for future mobile app)</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Latitude" type="number" value={form.latitude}
              onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} sx={{ flex: 1 }}
              inputProps={{ step: 'any' }}
            />
            <TextField
              label="Longitude" type="number" value={form.longitude}
              onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} sx={{ flex: 1 }}
              inputProps={{ step: 'any' }}
            />
            <TextField
              label="Radius (m)" type="number" value={form.geofence_radius_meters}
              onChange={e => setForm(f => ({ ...f, geofence_radius_meters: e.target.value }))} sx={{ flex: 1 }}
              inputProps={{ min: 100 }}
              helperText="Min 100m"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
            <Typography variant="subtitle2">Enforcement</Typography>
            <Tooltip title="Applies to employees assigned to this site, on every punch path — phone/PWA and kiosk, clock in and clock out. Hard Block refuses the punch; Soft Flag allows it and records the failure under Punch Activity. Managers and admins are never blocked, but their punches are recorded as overrides.">
              <HelpIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
            <Tooltip title="Requires the punch to come from an address in this site's IP Whitelist. Has no effect until at least one IP rule is added. Offline punches replayed later skip this check — the replay comes from wherever the phone regained signal.">
              <FormControlLabel
                control={<Switch checked={form.ip_enforcement_enabled} onChange={e => setForm(f => ({ ...f, ip_enforcement_enabled: e.target.checked }))} />}
                label="IP Restriction"
              />
            </Tooltip>
            {form.ip_enforcement_enabled && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mode</InputLabel>
                <Select value={form.ip_enforcement_mode} label="Mode" onChange={e => setForm(f => ({ ...f, ip_enforcement_mode: e.target.value }))}>
                  <MenuItem value="hard">Hard Block</MenuItem>
                  <MenuItem value="soft">Soft Flag</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
            <Tooltip title="Requires the punch to come from within the radius of this site's coordinates. Has no effect until latitude and longitude are set above. On Hard Block, an employee who denies location permission cannot punch at all.">
              <FormControlLabel
                control={<Switch checked={form.geo_enforcement_enabled} onChange={e => setForm(f => ({ ...f, geo_enforcement_enabled: e.target.checked }))} />}
                label="Geofence"
              />
            </Tooltip>
            {form.geo_enforcement_enabled && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mode</InputLabel>
                <Select value={form.geo_enforcement_mode} label="Mode" onChange={e => setForm(f => ({ ...f, geo_enforcement_mode: e.target.value }))}>
                  <MenuItem value="hard">Hard Block</MenuItem>
                  <MenuItem value="soft">Soft Flag</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          {((form.ip_enforcement_enabled && form.ip_enforcement_mode === 'hard') ||
            (form.geo_enforcement_enabled && form.geo_enforcement_mode === 'hard')) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Hard Block also applies to clocking <strong>out</strong>. An employee who leaves
              the site while still on the clock will not be able to clock out on their own —
              a manager or admin has to do it for them. Use Soft Flag if you would rather see
              off-site punches than block them.
            </Alert>
          )}

          {form.ip_enforcement_enabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              IP restriction does nothing until you add at least one address or range under
              this site&apos;s <strong>IP Whitelist</strong> tab.
            </Alert>
          )}

          {form.geo_enforcement_enabled && !(form.latitude && form.longitude) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Geofencing does nothing until this site has a latitude and longitude.
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel shrink>Manual time entries</InputLabel>
              <Select
                value={form.manual_entry_mode}
                label="Manual time entries"
                notched
                onChange={e => setForm(f => ({ ...f, manual_entry_mode: e.target.value }))}
              >
                <MenuItem value="off">Allowed</MenuItem>
                <MenuItem value="flag">Allowed — flag and notify</MenuItem>
                <MenuItem value="block">Blocked for employees</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Enforcing IP or location on clock in/out does nothing if someone can simply type the shift instead — a typed entry has no network or GPS behind it. 'Flag' still records the hours (never withhold worked time) but tells you they were entered by hand. 'Blocked' stops employees adding their own entries here; managers and admins always can, which is how a missed punch gets fixed.">
              <HelpIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          {form.manual_entry_mode === 'block' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Employees assigned to this site will not be able to add a time entry
              themselves — a missed punch has to be fixed by a manager. Choose
              <strong> Allowed — flag and notify</strong> if you would rather see them
              than block them.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Kiosk</Typography>
          <FormControlLabel
            control={<Switch checked={form.kiosk_enabled} onChange={e => setForm(f => ({ ...f, kiosk_enabled: e.target.checked }))} />}
            label="Enable kiosk mode for this site"
          />
          {form.kiosk_enabled && (
            <FormControlLabel
              control={<Switch checked={form.kiosk_photo_required} onChange={e => setForm(f => ({ ...f, kiosk_photo_required: e.target.checked }))} />}
              label="Require photo on clock-in/out"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSite}>{selectedSite ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog (IP rules, employees, devices) */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSite?.name}
          <Typography variant="body2" color="text.secondary">
            {[selectedSite?.address, selectedSite?.city, selectedSite?.state].filter(Boolean).join(', ')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 1 }}>
            <Tab icon={<WifiIcon />} label="IP Whitelist" iconPosition="start" />
            <Tab icon={<PeopleIcon />} label="Employees" iconPosition="start" />
            <Tab icon={<DevicesIcon />} label="Kiosk Devices" iconPosition="start" />
            <Tab icon={<FactCheckIcon />} label="Punch Activity" iconPosition="start" />
          </Tabs>

          {/* IP Whitelist Tab */}
          <TabPanel value={detailTab} index={0}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
              <TextField
                label="IP Address or CIDR" size="small" value={newIpCidr}
                onChange={e => setNewIpCidr(e.target.value)} placeholder="e.g. 10.20.20.0/24"
                sx={{ flex: 2 }}
              />
              <TextField
                label="Label (optional)" size="small" value={newIpLabel}
                onChange={e => setNewIpLabel(e.target.value)} placeholder="e.g. Office WiFi"
                sx={{ flex: 1 }}
              />
              <Button variant="contained" onClick={handleAddIpRule} startIcon={<AddIcon />}>Add</Button>
            </Box>

            {ipRules.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No IP rules configured. All IPs are allowed when IP enforcement is enabled with no rules.
              </Typography>
            ) : (
              <List dense>
                {ipRules.map(rule => (
                  <ListItem key={rule.id}>
                    <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <ListItemText
                      primary={String(rule.ip_cidr)}
                      secondary={rule.label || null}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteIpRule(rule.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </TabPanel>

          {/* Employees Tab */}
          <TabPanel value={detailTab} index={1}>
            {employeeListScoped && allEmployees.filter(e => e.id !== user?.employeeId).length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No managed employees assigned — contact an admin.
              </Alert>
            )}
            {employeeListScoped && allEmployees.filter(e => e.id !== user?.employeeId).length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Only employees you manage are listed. Others assigned to this site are hidden — ask an admin or get the View all employees permission to see them.
              </Alert>
            )}
            {unassignedEmployees.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel>Assign Employee</InputLabel>
                  <Select
                    label="Assign Employee"
                    value=""
                    onChange={e => handleAssignEmployee(e.target.value)}
                  >
                    {unassignedEmployees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.last_name}, {emp.first_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {siteEmployees.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No employees assigned to this site
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Kiosk PIN</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {siteEmployees.map(emp => (
                    <TableRow key={emp.employee_id}>
                      <TableCell>{emp.last_name}, {emp.first_name}</TableCell>
                      <TableCell>
                        {emp.has_pin ? (
                          <Chip label="PIN Set" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="No PIN" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleOpenPinDialog(emp)}>
                          {emp.has_pin ? 'Reset PIN' : 'Set PIN'}
                        </Button>
                        {emp.has_pin && (
                          <Button size="small" color="warning" onClick={() => handleClearPin(emp.employee_id)}>
                            Clear PIN
                          </Button>
                        )}
                        <IconButton size="small" color="error" onClick={() => handleUnassignEmployee(emp.employee_id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabPanel>

          {/* Kiosk Devices Tab */}
          <TabPanel value={detailTab} index={2}>
            {!selectedSite?.kiosk_enabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Kiosk mode is not enabled for this site. Enable it in site settings to register devices.
              </Alert>
            )}

            {selectedSite?.kiosk_enabled && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Device Name" size="small" value={newDeviceName}
                  onChange={e => setNewDeviceName(e.target.value)} placeholder="e.g. Front Desk iPad"
                  sx={{ flex: 1 }}
                />
                <Button variant="contained" onClick={handleRegisterDevice} startIcon={<AddIcon />}>Register</Button>
              </Box>
            )}

            {newDeviceUrl && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Device registered! Scan the QR code or open the URL on the tablet:</Typography>

                {newDeviceQr && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <img src={newDeviceQr} alt="Kiosk QR Code" style={{ width: 200, height: 200 }} />
                  </Box>
                )}

                <TextField
                  inputRef={kioskUrlInputRef}
                  fullWidth
                  size="small"
                  value={newDeviceUrl}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                    endAdornment: (
                      <IconButton size="small" onClick={handleCopyUrl}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                  onFocus={(e) => e.target.select()}
                />
              </Alert>
            )}

            {kioskDevices.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No kiosk devices registered
              </Typography>
            ) : (
              <List dense>
                {kioskDevices.map(dev => (
                  <ListItem key={dev.id}>
                    <DevicesIcon sx={{ mr: 1, color: dev.is_active ? 'success.main' : 'text.disabled' }} />
                    <ListItemText
                      primary={dev.device_name}
                      secondary={
                        dev.is_active
                          ? `Last seen: ${dev.last_heartbeat_at ? new Date(dev.last_heartbeat_at).toLocaleString() : 'Never'}`
                          : 'Deactivated'
                      }
                    />
                    <ListItemSecondaryAction>
                      {dev.is_active && (
                        <Button size="small" color="error" onClick={() => handleDeactivateDevice(dev.id)}>
                          Deactivate
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </TabPanel>

          {/* Punch Activity Tab */}
          <TabPanel value={detailTab} index={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={punchFlaggedOnly}
                    onChange={e => setPunchFlaggedOnly(e.target.checked)}
                  />
                }
                label="Flagged only"
              />
              <Tooltip title="Every clock in/out at this site for the last 14 days, with the result of its IP and location checks. Flagged means a check failed (soft enforcement lets those through), the punch was refused outright, or a manager/admin overrode enforcement, or an offline punch that had to be recorded at sync time.">
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            {punchEventsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
            ) : punchEvents.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {punchFlaggedOnly ? 'No flagged punches in the last 14 days' : 'No punch activity in the last 14 days'}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Punch</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Network</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {punchEvents.map(ev => (
                      <TableRow key={ev.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(ev.punch_time).toLocaleString()}
                          {ev.punch_refused && ev.source === 'offline_sync' && (
                            <Typography variant="caption" display="block" color="error">
                              Device time {claimedDeviceTime(ev)}
                              <Tooltip title="The time the employee's device recorded for this punch. The server refused it, so no hours were recorded for it. A manager needs to add or correct the time as a manual entry.">
                                <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            </Typography>
                          )}
                          {!ev.punch_refused && offlineTimeNotHonoured(ev) && (
                            <Typography variant="caption" display="block" color="error">
                              Device time {offlineTimeNotHonoured(ev).deviceTime} not used - {offlineTimeNotHonoured(ev).reason}
                              <Tooltip title="This punch was queued offline but arrived without a valid sync token (an app version from before this update, a token older than 72 hours, many punches in one offline stretch, or one that could not be verified), so it was recorded at the time it reached the server, shown above. Confirm the actual time with the employee and correct the time entry (Edit Entry: Start Time / End Time) before payroll; the employee's manager was notified. A sync-token verification flag alone does not mean the employee did anything wrong; common causes are an app update, a server key change, a slow phone clock, or a long time offline.">
                                <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            </Typography>
                          )}
                          {ev.source === 'offline_sync' && ev.received_at && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Recorded offline, synced {new Date(ev.received_at).toLocaleString()}
                              <Tooltip title="This punch was made on the employee's device without a connection. The time shown is when they actually punched; it synced to the server at the time shown here.">
                                <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{ev.last_name}, {ev.first_name}</TableCell>
                        <TableCell>{ev.punch_type === 'clock_in' ? 'In' : 'Out'}</TableCell>
                        <TableCell>{ev.source}</TableCell>
                        <TableCell>
                          {ev.ip_check_passed === null ? (
                            <Tooltip title="No IP check applied — the site has no IP rules, or this was an offline punch replayed from another network."><span>—</span></Tooltip>
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={ev.ip_check_passed ? 'success' : 'error'}
                              label={ev.ip_check_passed ? 'On network' : 'Off network'}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {ev.geo_check_passed === null ? (
                            <Tooltip title="No geofence check applied — the site has no coordinates or geo enforcement is off."><span>—</span></Tooltip>
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={ev.geo_check_passed ? 'success' : 'error'}
                              label={ev.geo_check_passed ? 'In fence' : 'Out of fence'}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={ev.notes || ''}>
                            <span>
                              {ev.punch_refused ? (
                                <Chip size="small" color="error" label="Refused" />
                              ) : ev.enforcement_overridden ? (
                                <Chip size="small" color="warning" label="Override" />
                              ) : (
                                <Chip size="small" color="success" variant="outlined" label="Accepted" />
                              )}
                            </span>
                          </Tooltip>
                          {ev.open_shift_left && (
                            <Typography variant="caption" display="block" color="error">
                              Clock-in left open
                              <Tooltip title="This clock-out was refused, so the employee is still clocked in. Add the shift as a manual entry (ending at the device time shown), then use Cancel Clock-In on the Dashboard's Currently Clocked In card, or the open clock-in keeps running.">
                                <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PIN Dialog */}
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Set Kiosk PIN — {pinEmployee?.first_name} {pinEmployee?.last_name}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="4-digit PIN" fullWidth sx={{ mt: 1 }}
            value={pinValue} onChange={e => { if (/^\d{0,4}$/.test(e.target.value)) setPinValue(e.target.value); }}
            inputProps={{ maxLength: 4, inputMode: 'numeric', pattern: '[0-9]*' }}
          />
          <Button size="small" sx={{ mt: 1 }} onClick={generateRandomPin}>
            Generate Random PIN
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSetPin} disabled={pinValue.length !== 4}>Set PIN</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialogComponent />
    </Box>
  );
}
